import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { Divider, TextField, Typography } from '@material-ui/core';
import SettingsInputCompositeOutlinedIcon from '@material-ui/icons/SettingsInputCompositeOutlined';
import { Autocomplete } from '@material-ui/lab';

import Card from '../../../common/card/Card';
import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import {
  useFindProductComponents,
  useUpdateProductDesignInput,
  useUpdateProductDesignOutput,
  useUpdateProductDesignSpec,
  useUpdateProductUserNeed,
  useUpdateProductValidation,
  useUpdateProductVerification
} from '../Product.hooks';
import {
  ProductComponent,
  ProductDesignInput,
  ProductDesignOutput,
  ProductDesignSpec,
  ProductUserNeed,
  ProductValidation,
  ProductVerification
} from '../Product.interface';

const StyledCard = styled(Card)`
  .MuiSelect-icon {
    display: none;
  }

  .MuiDivider-light {
    margin-top: 10px;
  }

  .title {
    height: 16px;
    line-height: 14.06px;
    font-size: 12px;
    font-weight: bold;
  }

  .MuiInput-root {
    border: 1px solid rgb(204, 204, 204);
    width: 100%;
    border-radius: 8px;
    padding: 2px 8px;
  }

  .MuiInput-input {
    background-color: white;
  }

  .MuiAutocomplete-tag {
    margin: 2px;
    background-color: #f0f1f7;
  }
  .MuiAutocomplete-endAdornment {
    display: none;
  }
`;

const ProductsSubSectionComponentCard: React.FC<{
  productId: number;
  productSubSection:
    | ProductUserNeed
    | ProductDesignInput
    | ProductDesignOutput
    | ProductVerification
    | ProductValidation
    | ProductDesignSpec;
  readOnlyComponents?: Partial<ProductComponent>[]; // component override for versioning display
}> = ({ productSubSection, productId, readOnlyComponents }) => {
  const history = useHistory();

  const [components, setComponents] = useState(
    readOnlyComponents || productSubSection?.components || []
  );
  useEffect(() => {
    if (readOnlyComponents) {
      setComponents(readOnlyComponents);
    } else if (productSubSection?.components) {
      setComponents(productSubSection.components);
    } else {
      setComponents([]);
    }
  }, [readOnlyComponents, productSubSection]);

  const { updateProductUserNeed } = useUpdateProductUserNeed(
    productId,
    productSubSection.id || 0
  );

  const { updateProductValidation } = useUpdateProductValidation(
    productId,
    productSubSection.id
  );

  const { updateProductDesignInput } = useUpdateProductDesignInput(
    productId,
    productSubSection.id
  );

  const { updateProductDesignOutput } = useUpdateProductDesignOutput(
    productId,
    productSubSection.id
  );

  const { updateProductDesignSpec } = useUpdateProductDesignSpec(
    productId,
    productSubSection.id
  );

  const { updateProductVerification } = useUpdateProductVerification(
    productId,
    productSubSection.id
  );

  const { productComponents } = useFindProductComponents(productId, true);
  const hasComponentsConfigured = productComponents && productComponents.length;
  const productSubSectionComponents =
    readOnlyComponents || productSubSection?.components || [];
  const { isFieldDisabled } = useTaskEntityFields(productSubSection.task);
  return (
    <StyledCard>
      <CardTitle>
        <SettingsInputCompositeOutlinedIcon />
        Components
      </CardTitle>
      <Divider light />
      <CardContent padding='medium'>
        <Typography className='title'>Assign Component(s)</Typography>
        <Autocomplete
          multiple
          className='auto-complete'
          fullWidth
          disabled={
            Boolean(readOnlyComponents) || isFieldDisabled('components')
          }
          filterSelectedOptions
          value={productSubSectionComponents}
          options={(productComponents || []).filter(component => {
            return !components.some(c => c.id === component.id);
          })}
          getOptionLabel={option => option.name || ''}
          onChange={(e, components) => {
            const delta = {
              id: productSubSection.id,
              components: components as ProductComponent[]
            };
            productSubSection.components = delta.components;
            setComponents(components);
            history.location.pathname.split('/')[4] === 'validations'
              ? updateProductValidation(delta)
              : history.location.pathname.split('/')[4] === 'design-inputs'
              ? updateProductDesignInput(delta)
              : history.location.pathname.split('/')[4] === 'design-outputs'
              ? updateProductDesignOutput(delta)
              : history.location.pathname.split('/')[4] === 'design-specs'
              ? updateProductDesignSpec(delta)
              : history.location.pathname.split('/')[4] === 'user-needs'
              ? updateProductUserNeed(delta)
              : history.location.pathname.split('/')[4] === 'verifications'
              ? updateProductVerification(delta)
              : (() => {})(); // do nothing
          }}
          renderInput={params => (
            <TextField
              {...params}
              fullWidth
              variant='outlined'
              helperText={
                !hasComponentsConfigured && (
                  <em>
                    No component defined for product, please go to the{' '}
                    <Link to={{ pathname: `/portal/products/${productId}` }}>
                      product settings page
                    </Link>{' '}
                    to add components.
                  </em>
                )
              }
            />
          )}
        />
      </CardContent>
    </StyledCard>
  );
};

export default ProductsSubSectionComponentCard;
